import { useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { getBoardId } from '@atlassian/jira-common-navigation/src/get-scope-data';
import { useSprintField } from '@atlassian/jira-issue-field-sprint/src/services/sprint-field/index.tsx';
import type { SprintValue } from '@atlassian/jira-issue-shared-types/src/common/types/sprint-type.tsx';
import { SPRINT_TYPE } from '@atlassian/jira-platform-field-config';
import { createClipboardHelperComponent } from '../../../clipboard-handler/create-clipboard-helper-component';
import { useClipboardHandler } from '../../../clipboard-handler/use-clipboard-handler';
import {
	assertClipboardDataIsCompatible,
	assertClipboardHasRichFormat,
} from '../../../clipboard-handler/utils';
import type {
	ClipboardItem,
	ListIssueFieldTransformed,
	ListIssueForCopyPaste,
	PasteHandler,
	PasteMetadata,
} from '../../../types';
import { sprintSupportedTypes } from '../supported-field-types';
import type { TransformedSprintFieldOption } from '../types';

const isTransformedSprintOption = (option: unknown): option is TransformedSprintFieldOption =>
	option != null &&
	typeof option === 'object' &&
	'sprintId' in option &&
	'name' in option &&
	'state' in option;

const isTransformedSprint = (fieldValue: unknown): fieldValue is TransformedSprintFieldOption[] =>
	Array.isArray(fieldValue) && fieldValue.every(isTransformedSprintOption);

const isSprintValue = (fieldValue: unknown): fieldValue is SprintValue[] =>
	Array.isArray(fieldValue) &&
	fieldValue.every(
		(option) =>
			option != null &&
			typeof option === 'object' &&
			'id' in option &&
			'name' in option &&
			'state' in option &&
			'boardId' in option,
	);
const emptyValue: [] = [];

const getPastedValue = (
	clipboardItem: ClipboardItem,
	metadata: PasteMetadata | null,
): SprintValue[] => {
	if (
		!assertClipboardHasRichFormat(clipboardItem) ||
		!assertClipboardDataIsCompatible(metadata, sprintSupportedTypes)
	) {
		return [];
	}

	if (!isSprintValue(metadata?.fieldValue)) {
		return [];
	}

	return metadata?.fieldValue ?? [];
};

const toSprintValue = (sprint: TransformedSprintFieldOption): SprintValue => ({
	id: Number(sprint.sprintId),
	name: String(sprint.name),
	state: String(sprint.state),
	boardId: Number(getBoardId(window.location.href)),
});

const useSprintClipboardHandler: PasteHandler = (
	issue: ListIssueForCopyPaste,
	field: ListIssueFieldTransformed,
) => {
	const [{ value }, { saveValue }] = useSprintField({
		issueKey: issue.key,
		fieldKey: field.fieldId,
		initialValue: isTransformedSprint(field.value) ? field.value.map(toSprintValue) : emptyValue,
	});

	const saveValueFn = useCallback(
		(valueToSave: SprintValue[] | null, meta: null, analyticsEvent: UIAnalyticsEvent) =>
			/**
			 * Wrap saveValue to make sure the saved value is never set to `null`
			 */
			saveValue(valueToSave ?? [], meta, analyticsEvent),
		[saveValue],
	);

	return useClipboardHandler({
		issue,
		field,
		currentStringValue: value?.map((sprint) => sprint.name).join(', ') || '',
		currentValue: value,
		getPastedValue,
		saveValue: saveValueFn,
		emptyValue,
	});
};

const SprintClipboardHelper = createClipboardHelperComponent(useSprintClipboardHandler);

export const sprintClipboardHandler = {
	[SPRINT_TYPE]: SprintClipboardHelper,
};
